import { CheckIcon } from '@heroicons/react/outline';
import clsx from 'clsx';
import _ from 'lodash';
import * as React from 'react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Button } from '@/components/Elements';
import { BILLING_MODE_MONTH, BILLING_MODE_YEAR, USAGE_LICENSED } from '@/config/appConstants';
import { useMe } from '@/hooks/useMe';
import { PricesTypes, ProductCardProps, ProductFeaturesTypes } from '@/types';
import {
  getProductPrice,
  getProductDetails,
  isFreemiumProduct,
  parseDate,
  parseCurrency,
  isPrepaidProduct,
  getBillingModePriceText,
  getBillingModePlanText,
  isAppSumo,
  isSuspendedProduct,
} from '@/utils/helpers';

const PromotionPriceWrap = styled.div`
  .card__price__promotion {
    display: inline-block;
    height: 40px;
    padding: 0 3px;
    margin-right: 5px;
    font-size: 0.5em;
    background: linear-gradient(
      to left top,
      transparent 47.75%,
      currentColor 49.5%,
      currentColor 50.5%,
      transparent 52.25%
    );
  }
`;

export const ProductCard = ({
  onPendingButtonAction,
  onIncompleteAction,
  onProductSelect,
  onRequestInfo,
  onActionButton,
  product,
  userCurrency,
  className,
  formatProductName = undefined,
  formatCredits = undefined,
  formatCategoryName = undefined,
  userBillingMode = BILLING_MODE_MONTH,
  currentPeriodEndDate,
  alreadyPurchased = false,
  alreadyPurchasedLink,
  isCancelled = false,
  isProcessing = false,
  isPending = false,
  isIncomplete = false,
  actionButtonLabel,
  hidePrice = product?.hidePrice || false,
  autoCheckout,
  showFeatures = true,
  isSticky = false,
  removePrice = false,
  activeBillingMode,
  promotionCode = '',
}: ProductCardProps) => {
  const { t, i18n } = useTranslation();
  const buttonCardRef = useRef<HTMLButtonElement>(null);
  const { getMe } = useMe();
  const me = getMe();

  const onDefaultRequestInfo = () => {
    onRequestInfo && onRequestInfo(product);
  };

  const onDefaultActionButton = () => {
    if (onActionButton) {
      onActionButton(product);
      return;
    }

    if (hidePriceNeeded) {
      onDefaultRequestInfo();
      return;
    }

    onProductSelect && onProductSelect(product);
  };

  const onDefaultPendingButtonAction = () => {
    if (onPendingButtonAction) {
      onPendingButtonAction(product);
      return;
    }
  };

  const onDefaulIncompleteAction = () => {
    if (onIncompleteAction) {
      onIncompleteAction();
      return;
    }
  };

  useEffect(() => {
    autoCheckout && buttonCardRef.current?.click();
  }, [autoCheckout]);

  if (!product) {
    return (
      <article className={className ? className : 'p-1 w-full md:w-1/2 xl:w-1/4 pt-8 rounded-lg'}>
        <p className="font-black text-xl uppercase tracking-tighter text-md-black text-center mt-20">
          {t('ProductDetail.productNotAvailable')}
        </p>
        <Button
          variant="primary"
          type="button"
          size="free"
          onClick={onDefaultRequestInfo}
          className="my-5 font-bold py-4 text-xl w-full rounded"
        >
          {t('ProductDetail.requestInfo')}
        </Button>
      </article>
    );
  }

  const getproductNameTranslated = (productName: string | ProductFeaturesTypes) => {
    return t(`ProductDetail.${productName}`) === `ProductDetail.${productName}`
      ? productName
      : t(`ProductDetail.${productName}`);
  };

  const isFreemium = isFreemiumProduct(product);
  const isFeatured = product.featured || false;
  const productName = getProductDetails(product, 'productName');
  const categoryName = getProductDetails(product, 'categoryName') as string;
  const categoryNameParsed = _.camelCase(categoryName.replace(/\s/g, ''));
  const categoryNameTranslated = t(`ProductDetail.${categoryNameParsed}`);
  const productNameTranslated = getproductNameTranslated(productName);

  const getPriceText = (
    price: number | bigint | undefined,
    hidePrice: boolean,
    isFreemium: boolean
  ) => {
    {
      if (price && !hidePrice) {
        return parseCurrency(i18n.language, userCurrency, price);
      }

      if (!isFreemium) {
        return t('ProductDetail.askUs');
      }

      return parseCurrency(i18n.language, userCurrency, 0);
    }
  };

  const getProductPriceWithPromotion = (productPrice: PricesTypes): number | bigint => {
    if (productPrice.promotionCode !== null) {
      return productPrice.amountWithPromotion as number | bigint;
    }
    return productPrice.amount as number | bigint;
  };

  const setHasPriceWithPromotion = (productPrice: PricesTypes, isFreemium: boolean): boolean => {
    return productPrice.promotionCode !== null && !isFreemium;
  };

  const productPriceObj = getProductPrice(
    product,
    USAGE_LICENSED,
    userCurrency,
    userBillingMode
  ) as PricesTypes;
  const hasPriceWithPromotion = setHasPriceWithPromotion(productPriceObj, isFreemium);

  const productPriceWithPromotion = getProductPriceWithPromotion(productPriceObj);
  const productPrice = productPriceObj.amount as number | bigint;

  const productFeatures = getProductDetails(product, 'features');
  const actionButton = actionButtonLabel || getProductDetails(product, 'actionButton');
  const hidePriceNeeded =
    product.hidePrice || hidePrice || userBillingMode === BILLING_MODE_YEAR || false;
  const prefix = 'ProductsFeatures.';

  const getActionButton = () => {
    if (isPending || isIncomplete) {
      return (
        <Button
          onClick={isPending ? onDefaultPendingButtonAction : onDefaulIncompleteAction}
          variant="primary"
          size="free"
          className={clsx('my-5 font-bold py-4 w-full rounded', {
            'text-xl': showFeatures,
            'text-base': !showFeatures,
          })}
        >
          {t('ProductDetail.pending')}
        </Button>
      );
    }

    if ((alreadyPurchased && userBillingMode == activeBillingMode) || isCancelled || isProcessing) {
      if (typeof alreadyPurchasedLink !== 'undefined' && !isProcessing) {
        return (
          <Button
            variant="secondary"
            size="free"
            className="my-5 font-bold py-4 text-xl w-full rounded"
            onClick={() => window.location.assign(alreadyPurchasedLink)}
          >
            {t('ProductDetail.goToProduct')}
          </Button>
        );
      }

      return (
        <Button disabled size="free" className="my-5 font-bold py-4 text-xl w-full rounded">
          {isCancelled ? `${t('ProductDetail.cancelling')} *` : ''}
          {isProcessing ? t('ProductDetail.processing') : ''}
          {alreadyPurchased && !isCancelled && !isProcessing
            ? t('ProductDetail.alreadyPurchased')
            : ''}
        </Button>
      );
    }

    return (
      <Button
        onClick={onDefaultActionButton}
        variant="primary"
        type="button"
        size="free"
        className="my-5 font-bold py-4 text-xl w-full rounded"
        ref={buttonCardRef}
        isLoading={false}
      >
        {getDefaultActionButtonLabel()}
      </Button>
    );
  };

  const getDefaultActionButtonLabel = () => {
    if (isFreemium) {
      return actionButtonLabel ? actionButtonLabel : t('ProductDetail.tryNow');
    }

    return hidePriceNeeded ? t('ProductDetail.writeUs') : actionButton;
  };

  const generateProductsFeaturesTranslation = (text: string): string => {
    let _item = t(`${prefix}${text}`);
    _item.startsWith(prefix) && (_item = _item.replace(prefix, ''));
    return _item;
  };

  const wantToPrintPrice = () =>
    (!promotionCode && !alreadyPurchased && !isProcessing) || !removePrice;
  const renderPrice = (
    productPrice: number | bigint | undefined,
    productPriceWithPromotion: number | bigint | undefined,
    hasPriceWithPromotion: boolean,
    hidePrice: boolean,
    isFreemium: boolean
  ) => {
    if (!hasPriceWithPromotion || isFreemium) {
      return getPriceText(productPrice, hidePrice, isFreemium);
    }

    return (
      <PromotionPriceWrap>
        <div className="card__price__promotion">
          {getPriceText(productPrice, hidePrice, isFreemium)}
        </div>
        {getPriceText(productPriceWithPromotion, hidePrice, isFreemium)}
      </PromotionPriceWrap>
    );
  };
  return (
    <article
      className={
        className
          ? className
          : clsx('p-1 w-full md:w-1/2 xl:w-1/4 pt-8 rounded-lg', { 'mt-4 lg:mt-0': isFeatured })
      }
    >
      <div
        className={clsx('bg-md-whiteAlt relative text-md-blue rounded-lg shadow-lg h-full', {
          'border-2 border-md-purple rounded-tr-none': isFeatured,
          'lg:rounded-tr-lg': isSticky,
        })}
      >
        {isFeatured && (
          <div className="md__product__featured__badge">{t('ProductDetail.featured')}</div>
        )}
        <header
          className={clsx('bg-md-white text-center', {
            'py-4 px-4 rounded-tl-lg rounded-tr-lg': !isFeatured,
            'p-[14px] rounded-tl-[0.4rem]': isFeatured,
            'bg-md-red': isCancelled,
            'bg-md-gullGray': isProcessing,
            'bg-md-lightPurple': isPending,
            'rounded-bl-lg rounded-br-lg': !showFeatures,
            'lg:rounded-tr-lg': isSticky,
          })}
        >
          <p
            className={clsx('card__category__name font-black text-2xl uppercase tracking-tighter', {
              'text-md-purple': !isCancelled,
            })}
          >
            {formatCategoryName
              ? formatCategoryName(categoryNameTranslated)
              : isPrepaidProduct(product) || isSuspendedProduct(product)
              ? categoryNameTranslated
              : categoryName}
          </p>
          {!formatCredits && (
            <p
              className={clsx('text-base font-black mt-0 text-md-softGray', {
                'text-md-blue': !isCancelled,
              })}
            >
              {formatProductName
                ? formatProductName(productName)
                : !isSuspendedProduct(product)
                ? productNameTranslated
                : '-'}
            </p>
          )}
          {formatCredits && formatCredits(product)}
          <div
            className={clsx('card__price text-4xl font-black mt-5', {
              'text-md-blue': !isCancelled,
            })}
          >
            {wantToPrintPrice() ? (
              formatCredits ? (
                <div
                  className={clsx('p-4 mt-5 bg-md-silver', {
                    'text-center': hidePrice,
                    'flex items-center justify-between': !hidePrice,
                  })}
                >
                  <p className="text-2xl font-black tracking-tighter">
                    {getPriceText(productPrice, hidePrice, isFreemium)}
                  </p>
                  {!hidePrice && (
                    <div className="text-md-softGray text-center text-sm font-normal">
                      <p className="m-0 p-0">{t('BillingMode.unique')}</p>
                      <p className="m-0 p-0">{t('productTypes.smsMarketing.taxesDisclaimer')}**</p>
                    </div>
                  )}
                </div>
              ) : (
                renderPrice(
                  productPrice,
                  productPriceWithPromotion,
                  hasPriceWithPromotion,
                  hidePrice,
                  isFreemium
                )
              )
            ) : (
              <div className="text-center text-sm font-normal">
                {isAppSumo(me)
                  ? t('BillingMode.unique')
                  : t(getBillingModePlanText(userBillingMode))}
              </div>
            )}
          </div>
          {wantToPrintPrice() && !formatCredits && (
            <p
              className={clsx('uppercase text-xs', {
                'text-md-blue': !isCancelled,
              })}
            >
              {t(getBillingModePriceText(userBillingMode))} *{hasPriceWithPromotion && <>/ **</>}
            </p>
          )}
          {getActionButton()}
          {isCancelled && currentPeriodEndDate && (
            <p>
              {t('ProductDetail.cancellingDisclaimer')}
              <span>{parseDate(i18n.language, currentPeriodEndDate)}</span>
            </p>
          )}
        </header>
        {showFeatures && (
          <section
            className={clsx('bg-md-whiteAlt rounded-bl-lg rounded-br-lg', {
              'px-5 pt-5 pb-8': !isFeatured,
              'px-[14px] pb-[30px] pt-[22px]': isFeatured,
            })}
          >
            <div>
              <p className="font-black text-xs text-md-blue uppercase mb-3">
                {typeof productFeatures !== 'string'
                  ? generateProductsFeaturesTranslation(productFeatures.title)
                  : ''}
              </p>
              <ol>
                {typeof productFeatures !== 'string'
                  ? productFeatures.bullets.map((bullet: string, index: number) => (
                      <li className="flex mb-3" key={index}>
                        <CheckIcon className="h-5 min-w-[20px] text-md-green" aria-hidden="true" />
                        <div className="ml-1 text-md-blue font-secondary text-xs">
                          {generateProductsFeaturesTranslation(bullet)}
                        </div>
                      </li>
                    ))
                  : ''}
              </ol>
            </div>
          </section>
        )}
      </div>
    </article>
  );
};
